// ============================================================================
// JARVISTRAVEL - TOUR CONTROLLER
// Sequences CoachMarks over `data-tour` anchors (JAR-263). Reachability is
// resolved once on mount: steps whose anchor isn't in the DOM are dropped so
// progress reads true (a 5-step tour with one missing anchor is "1 of 4").
// When one anchor name matches several elements (mobile BottomNav + desktop
// TopNav render the same targets), the visible one — non-zero rect — wins.
// Persistence and analytics stay with the caller via onComplete/onDismiss.
// ============================================================================

import { useEffect, useRef, useState } from 'react';
import { CoachMark } from './CoachMark';

export interface TourStep {
  id: string;
  /** Matches the target's `data-tour` attribute. */
  anchor: string;
  title: string;
  body: string;
}

interface TourControllerProps {
  steps: TourStep[];
  /** The user finished the tour (Done) — or no anchors existed to show. */
  onComplete: () => void;
  /** The user left early (Skip / Escape / scrim); receives the step index left from. */
  onDismiss: (atStep: number, step?: TourStep) => void;
  /** A step became visible (for step_viewed analytics). */
  onStepChange?: (index: number, step?: TourStep) => void;
}

/** The visible element for an anchor name; falls back to the first match. */
function resolveAnchor(name: string): Element | null {
  const els = Array.from(document.querySelectorAll(`[data-tour="${name}"]`));
  const visible = els.find((el) => {
    const r = el.getBoundingClientRect();
    return r.width > 0 || r.height > 0;
  });
  return visible ?? els[0] ?? null;
}

export function TourController({ steps, onComplete, onDismiss, onStepChange }: TourControllerProps) {
  // Snapshot reachable steps AFTER mount — during the first render the anchors
  // in the same tree aren't committed to the DOM yet, so querying then would
  // see nothing and silently end the tour.
  const [reachable, setReachable] = useState<TourStep[] | null>(null);
  const [index, setIndex] = useState(0);
  const [active, setActive] = useState(true);

  const completedEmpty = useRef(false);
  useEffect(() => {
    const found = steps.filter((s) => resolveAnchor(s.anchor) !== null);
    setReachable(found);
    // Nothing to point at (fresh empty screen, changed layout) — end quietly so
    // the caller still marks the tour seen and it never haunts a later session.
    if (found.length === 0 && !completedEmpty.current) {
      completedEmpty.current = true;
      onComplete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one mount snapshot; steps are static per tour
  }, []);

  const current = active && reachable ? reachable[index] : undefined;
  const anchor = current ? resolveAnchor(current.anchor) : null;

  // Bring the anchor into view, then report the step for analytics.
  useEffect(() => {
    if (!current) return;
    anchor?.scrollIntoView?.({ block: 'center', behavior: 'auto' });
    onStepChange?.(index, current);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- per step-view, not per callback identity
  }, [index, current?.id]);

  if (!current || !reachable) return null;

  const handleNext = () => {
    if (index + 1 >= reachable.length) {
      setActive(false);
      onComplete();
    } else {
      setIndex(index + 1);
    }
  };

  const handleDismiss = () => {
    setActive(false);
    onDismiss(index, current);
  };

  return (
    <CoachMark
      anchor={anchor}
      title={current.title}
      body={current.body}
      step={index + 1}
      totalSteps={reachable.length}
      onNext={handleNext}
      onDismiss={handleDismiss}
    />
  );
}
